import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, IconButton } from '@mui/material';
import React, { useMemo, useState } from 'react';
import EmailPreviewContent from './emailPreview/EmailPreviewContent';
import EmailPreviewHeader from './emailPreview/EmailPreviewHeader';
import { buildSanitizedEmailPreviewHtml } from './emailPreview/emailPreviewHtmlUtils';
import { useEmailPreviewIconPosition } from './emailPreview/useEmailPreviewIconPosition';
import StyledModal from './styles/StyledModal';

export interface EmailPreviewData {
  to: string;
  subject: string;
  renderedEmail: {
    html: string;
    text?: string;
  };
}

interface EmailPreviewProps {
  email: EmailPreviewData;
  className?: string;
  highlightedLinkId?: string | null;
}

/**
 * EmailPreview component that displays email metadata and HTML content
 * Can be expanded to a full modal view
 */
const EmailPreview: React.FC<EmailPreviewProps> = ({ email, className, highlightedLinkId }) => {
  const [modalOpen, setModalOpen] = useState(false);

  const sanitizedEmailHTML = useMemo(
    () => buildSanitizedEmailPreviewHtml(email.renderedEmail.html, highlightedLinkId),
    [email.renderedEmail.html, highlightedLinkId],
  );

  const { iframeRef, modalIframeRef, handleIframeLoad } = useEmailPreviewIconPosition(
    highlightedLinkId,
    sanitizedEmailHTML,
  );

  return (
    <>
      <Box
        className={`react-EmailPreview ${className || ''}`.trim()}
        sx={{
          border: '1px solid #d9e1da',
          borderRadius: '16px',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '600px',
        }}
      >
        <Box
          sx={{
            p: 2.5,
            px: 3,
            borderBottom: '1px solid #d9e1da',
            backgroundColor: '#ffffff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexShrink: 0,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
            <EmailPreviewHeader to={email.to} subject={email.subject} />
          </Box>
          <IconButton
            onClick={() => setModalOpen(true)}
            sx={{
              ml: 1,
              flexShrink: 0,
              backgroundColor: '#ffffff',
              border: '1px solid #d9e1da',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
            size="small"
            title="Open in modal"
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            position: 'relative',
            minHeight: 0,
            backgroundColor: '#f5f5f5',
          }}
        >
          <EmailPreviewContent
            sanitizedEmailHTML={sanitizedEmailHTML}
            iframeRef={iframeRef}
            onIframeLoad={handleIframeLoad}
          />
        </Box>
      </Box>

      <StyledModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Email Preview"
        fullWidth
        maxWidth="md"
        noPadding
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '80vh',
            maxHeight: '800px',
          }}
        >
          <Box
            sx={{
              p: 2.5,
              px: 3,
              borderBottom: '1px solid #d9e1da',
              backgroundColor: '#ffffff',
              flexShrink: 0,
            }}
          >
            <EmailPreviewHeader to={email.to} subject={email.subject} />
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              position: 'relative',
              minHeight: 0,
              backgroundColor: '#f5f5f5',
            }}
          >
            <EmailPreviewContent
              sanitizedEmailHTML={sanitizedEmailHTML}
              iframeRef={modalIframeRef}
              isModal
              onIframeLoad={handleIframeLoad}
            />
          </Box>
        </Box>
      </StyledModal>
    </>
  );
};

export default EmailPreview;
